import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { isSameDay, parseISO } from 'date-fns';
import { getDateRangeForPreset } from '@sporkbytes/time-utils';
import startCase from 'lodash.startcase';
import { DateTimePicker } from '../DateTimePicker';
import { SingleSelect } from '../SingleSelect';
import GridContainer from '../GridContainer';
const presets = [{
  type: 'option',
  value: 'allTime'
}, {
  type: 'option',
  value: 'allUpcoming'
}, {
  type: 'option',
  value: 'allPast'
}, {
  type: 'group',
  value: 'days',
  options: [{
    value: 'today'
  }, {
    value: 'tomorrow'
  }, {
    value: 'yesterday'
  }]
}, {
  type: 'group',
  value: 'weeks',
  options: [{
    value: 'upcomingWeek'
  }, {
    value: 'thisWeek'
  }, {
    value: 'nextWeek'
  }, {
    value: 'lastWeek'
  }]
}, {
  type: 'group',
  value: 'payoutPeriods',
  options: [{
    value: 'thisPayoutPeriod'
  }, {
    value: 'nextPayoutPeriod'
  }, {
    value: 'lastPayoutPeriod'
  }]
}, {
  type: 'group',
  value: 'months',
  options: [{
    value: 'thisMonth'
  }, {
    value: 'monthToDate'
  }, {
    value: 'nextMonth'
  }, {
    value: 'lastMonth'
  }, {
    value: 'monthBeforeLast'
  }]
}, {
  type: 'group',
  value: 'quarters',
  options: [{
    value: 'thisQuarter'
  }, {
    value: 'quarterToDate'
  }, {
    value: 'nextQuarter'
  }, {
    value: 'lastQuarter'
  }]
}, {
  type: 'group',
  value: 'years',
  options: [{
    value: 'thisYear'
  }, {
    value: 'yearToDate'
  }, {
    value: 'nextYear'
  }, {
    value: 'lastYear'
  }]
}];

const convertToDate = date => date instanceof Date ? date : parseISO(date);

const findPresetForDateRange = ({
  beginDate,
  endDate
}, hiddenPresets) => {
  const availablePresets = getAvailablePresets(hiddenPresets).filter(preset => preset.type !== 'group').map(({
    value
  }) => value);
  return availablePresets.find(preset => {
    const dateRange = getDateRangeForPreset(preset);
    const beginDatesMatch = isSameDay(convertToDate(dateRange.beginDate), convertToDate(beginDate)) || !dateRange.beginDate && !beginDate;
    const endDatesMatch = isSameDay(convertToDate(dateRange.endDate), convertToDate(endDate)) || !dateRange.endDate && !endDate;
    return beginDatesMatch && endDatesMatch;
  });
};

const getAvailablePresets = hiddenPresets => presets.filter(preset => showPreset(preset.value, hiddenPresets)).flatMap(({
  options,
  ...preset
}) => {
  if (Array.isArray(options)) {
    return [preset, ...options.filter(option => showPreset(option.value, hiddenPresets))];
  }

  return preset;
});

const presetIsAvailable = (preset, hiddenPresets) => getAvailablePresets(hiddenPresets).map(({
  value
}) => value).includes(preset);

const showPreset = (preset, hiddenPresets) => {
  if (hiddenPresets) {
    return !hiddenPresets.includes(preset);
  }

  return true;
};

const useDateRange = (hiddenPresets, onChange, defaultDateRange) => {
  const [dateRange, setDateRange] = useState(() => {
    if (defaultDateRange.preset && presetIsAvailable(defaultDateRange.preset, hiddenPresets)) {
      return { ...defaultDateRange,
        ...getDateRangeForPreset(defaultDateRange.preset)
      };
    }

    return { ...defaultDateRange,
      preset: findPresetForDateRange(defaultDateRange, hiddenPresets)
    };
  });

  const setDateRangeProperty = field => value => {
    const availableDateFields = ['beginDate', 'endDate'];
    let newDateRange = { ...dateRange
    };

    if (field === 'preset' && presetIsAvailable(value, hiddenPresets)) {
      newDateRange = { ...newDateRange,
        ...getDateRangeForPreset(value),
        preset: value
      };
    } else if (availableDateFields.includes(field)) {
      const [otherDateField] = availableDateFields.filter(dateField => dateField !== field);
      const presetForDateRange = findPresetForDateRange({
        [field]: value,
        [otherDateField]: dateRange[otherDateField]
      }, hiddenPresets);
      newDateRange = { ...newDateRange,
        [field]: value === '' ? undefined : value,
        // empty strings must be converted to undefined values for consistency with proper date range handling
        preset: presetForDateRange
      };
    }

    onChange(newDateRange);
    setDateRange(newDateRange);
  };

  return [dateRange, setDateRangeProperty];
};

const DateSelector = ({
  children,
  defaultDateRange,
  hiddenPresets,
  onChange
}) => {
  const [dateRange, setDateRangeProperty] = useDateRange(hiddenPresets, onChange, defaultDateRange);
  return React.createElement(GridContainer, null, React.createElement(SingleSelect, {
    label: "Presets",
    value: dateRange.preset,
    options: getAvailablePresets(hiddenPresets),
    onChange: setDateRangeProperty('preset'),
    getOptionLabel: preset => startCase(preset.value),
    getOptionKey: preset => preset.value,
    getOptionType: preset => preset.type,
    getOptionValue: preset => preset.value
  }), React.createElement(DateTimePicker, {
    dateOnly: true,
    value: dateRange.beginDate,
    label: "Begin Date",
    maxDate: dateRange.endDate,
    clearable: true,
    onChange: setDateRangeProperty('beginDate')
  }), React.createElement(DateTimePicker, {
    dateOnly: true,
    value: dateRange.endDate,
    label: "End Date",
    minDate: dateRange.beginDate,
    clearable: true,
    onChange: setDateRangeProperty('endDate')
  }), children);
};

DateSelector.propTypes = {
  defaultDateRange: PropTypes.shape({
    beginDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    preset: PropTypes.string
  }),
  hiddenPresets: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired
};
DateSelector.defaultProps = {
  defaultDateRange: {},
  hiddenPresets: []
};
export default DateSelector;